/**
 * Sentry Browser SDK initialization (shared across all verticals)
 *
 * Initializes Sentry as early as possible in the application lifecycle.
 * DSN and environment are read from data attributes on the <html> tag.
 *
 * Usage: Import this file as the first line in each vertical's app.js:
 *   import '../../../common-assets/js/components/sentry_init';
 */

import * as Sentry from '@sentry/browser';

(function initSentry() {
    // Matches Cloudflare Zaraz proxied third-party chunks served as /static/{chunkId}.{hash}.js
    // (e.g. SourcePoint CMP live.json). Referenced in both denyUrls and the beforeSend frame scan
    // because denyUrls only inspects the outermost frame; the frame scan covers Sentry-wrapped fetches.
    const ZARAZ_PROXIED_CHUNK_RE = /\/static\/\d+\.[a-f0-9]+\.js/;

    const htmlElement = document.documentElement;
    const { sentryDsn: dsn, environment, siteCode } = htmlElement.dataset;

    // Skip initialization if DSN is not provided (dev/test environments)
    if (!dsn || dsn === '') {
        console.info('[Sentry] No DSN provided; skipping initialization.');
        return;
    }

    try {
        const isTest = environment === 'test';

        Sentry.init({
            dsn,
            environment: environment || 'production',

            // 100% in test/staging, 10% in production
            sampleRate: isTest ? 1 : 0.1,

            // Tracing and web vitals disabled — self-hosted Sentry has limited disk.
            // Re-enable once storage is confirmed sufficient.
            tracesSampleRate: 0,

            // Add site code as tag for filtering in Sentry UI
            initialScope: {
                tags: {
                    site_code: siteCode || 'unknown',
                },
            },

            // Remove BrowserTracing and Replay integrations to prevent web vitals
            // and session replay data being sent (high disk usage on self-hosted Sentry).
            integrations: (integrations) =>
                integrations.filter((i) => i.name !== 'BrowserTracing' && i.name !== 'Replay'),

            // Suppress errors from known third-party ad/outstream scripts and browser extensions
            denyUrls: [
                /^chrome-extension:\/\//, // Chrome extension injected scripts
                /^moz-extension:\/\//, // Firefox extension injected scripts
                /^safari-extension:\/\//, // Safari extension injected scripts
                /^safari-web-extension:\/\//, // Safari Web Extension injected scripts (iOS 18+)
                /\/outstream\/ANOutstreamVideo\.js/, // AppNexus/Xandr VPAID outstream player — loaded via Alma Content Router, not fixable
                /\/ats-nettiauto\/artifacts\//, // Alma Targeting System (ATS/Prebid.js + GAM) — currency fetch has no .catch(), not fixable
                /\/cdn-cgi\/zaraz\//, // Cloudflare Zaraz worker — internal 4xx fetch failures are Zaraz-side noise, not fixable in app code
                /api\.permutive\.com/, // Permutive analytics — network failures when blocked or offline, not fixable
                /\/tag\/js\/gpt\.js/, // Google Publisher Tag (GPT) loaded via Zaraz — network failures in Google's analytics code, not fixable
                ZARAZ_PROXIED_CHUNK_RE, // Cloudflare Zaraz proxied third-party chunks (e.g. SourcePoint CMP) served as /static/{chunkId}.{hash}.js
            ],

            // Filter out noise (third-party scripts, browser extensions)
            ignoreErrors: [
                'top.GLOBALS', // Legacy browser global error noise
                /^Network request failed\.?$/, // Exact transient network error only
                /^NetworkError when attempting to fetch resource\.?$/, // Firefox exact wording for transient fetch failures
                'ResizeObserver loop limit exceeded', // Benign browser/plugin observer warning
                'NS_ERROR_NOT_INITIALIZED', // Firefox: AppNexus VPAID player accesses video element after teardown
                /Blocked a frame with origin.*from accessing a cross-origin frame/, // GTM ad tag cross-origin frame access — fix in GTM container
                /Cannot read properties of null \(reading 'document'\)/, // Ad banner SDK (CrossScreenBannerFormat) reads iframe.document after frame is removed from DOM
                /null is not an object \(evaluating '[^']*\.document'\)/, // Safari wording of the same null-iframe.document access (e.g. Google getViewportGeometry from inline GTM bootstrap)
                /NotReadableError:.*I\/O read operation failed/, // Browser media API failures (camera/mic access denied, extensions, third-party scripts) — no stacktrace, unfixable (removed ^ to catch wrapped errors like "TypeError: NotReadableError")
                /Failed to track\b.*\bPageview\b.*\bNetwork request failed/i, // Permutive SDK error (Sentry wraps in "Non-Error promise rejection captured with value: ..." so no anchors)
                /Invalid call to runtime\.sendMessage\(\)/, // Safari Web Extension runtime API — thrown when extension messages a closed/navigated tab; iOS 18+ extension noise, no stacktrace
            ],

            // Only capture errors from our own domains (production + staging + branch)
            // Matches any subdomain: www.nettiauto.com, assets.nettiauto.com, etc.
            allowUrls: [
                /https?:\/\/[^/]*nettiauto\.com/,
                /https?:\/\/[^/]*nettimoto\.com/,
                /https?:\/\/[^/]*nettivene\.com/,
                /https?:\/\/[^/]*nettikone\.com/,
                /https?:\/\/[^/]*nettikaravaani\.com/,
                /https?:\/\/[^.]+\.ajomark\.dev/,
                /https?:\/\/[^.]+\.nettix-aws\.com/,
            ],

            // Attach user context if Alma Tunnus user is logged in
            beforeSend(event) {
                // Drop events from file:// context — headless bot/scraper running local HTML
                // (e.g. file:///tmp/scraper.html via a headless crawler).
                // allowUrls doesn't filter these because onunhandledrejection frames lack an origin.
                if (globalThis.location.protocol === 'file:') {
                    return null;
                }

                // Drop unfixable third-party fetch errors that bypass denyUrls.
                // When a rejected fetch fires inside Sentry's instrumented wrapper the outermost
                // frame is Sentry's own code, so denyUrls never gets a third-party URL to match.
                // We scan ALL frames across ALL exception values (chained/aggregate exceptions can
                // place the matching frame in values[1], values[2], etc., not just values[0]).
                const frames = (event.exception?.values ?? []).flatMap(
                    (v) => v?.stacktrace?.frames ?? [],
                );
                const isThirdPartyFetchNoise = frames.some((f) => {
                    const fn = f.filename ?? '';
                    return (
                        /\/ats-nettiauto\/artifacts\//.test(fn) || // ATS/Prebid.js currency fetch — no .catch()
                        ZARAZ_PROXIED_CHUNK_RE.test(fn) // Cloudflare Zaraz proxied scripts (e.g. SourcePoint CMP live.json)
                    );
                });
                if (isThirdPartyFetchNoise) {
                    return null;
                }

                // Check if user info is available in global scope (set by alma_tunnus.js)
                if (globalThis.almaTunnus !== undefined && globalThis.almaTunnus.user) {
                    const { id, username } = globalThis.almaTunnus.user;
                    // Do NOT send email, phone, or other PII
                    return { ...event, user: { id: id || undefined, username: username || undefined } };
                }

                return event;
            },
        });

        console.info('[Sentry] Initialized successfully', { environment, siteCode });
    } catch (error) {
        // Log init failure but don't block app
        console.error('[Sentry] Failed to initialize:', error);
    }
})();
